import {
  createAcceptedChoices,
  createDefaultGoogleConsent,
  createDeniedChoices,
  normalizeChoices,
  toGoogleConsent,
} from './consent';
import { resolveOptions } from './config';
import { widgetStyles } from './styles';
import type {
  ConsentChoices,
  ConsentEventDetail,
  ConsentWidgetApi,
  ConsentWidgetOptions,
  Decision,
  OptionalCategoryKey,
  ResolvedConsentWidgetOptions,
  StoredConsent,
} from './types';

type WidgetWindow = Window & typeof globalThis & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  GDConsent?: ConsentWidgetApi;
};

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;
const FOCUSABLE_SELECTOR = 'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export class ConsentWidget implements ConsentWidgetApi {
  private readonly options: ResolvedConsentWidgetOptions;
  private readonly consentWindow: WidgetWindow;
  private readonly dataLayer: unknown[];
  private host: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private state: StoredConsent | null = null;
  private mounted = false;
  private destroyed = false;
  private previousOverflow = '';
  private previouslyFocused: HTMLElement | null = null;

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-gd-consent-settings], [data-cookie-settings-trigger], [data-element="cookie-settings-trigger"]')) {
      event.preventDefault();
      this.openSettings();
    }
  };

  private readonly handleOpenEvent = (): void => this.openSettings();

  private readonly handleKeydown = (sourceEvent: Event): void => {
    if (!(sourceEvent instanceof KeyboardEvent)) return;
    const event = sourceEvent;
    const modal = this.getElement<HTMLElement>('[data-element="modal"]');
    if (!modal || modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSettings();
      return;
    }

    if (event.key !== 'Tab' || !this.shadow) return;
    const focusable = [...this.shadow.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)]
      .filter((element) => !element.closest('[hidden]'));
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && this.shadow.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.shadow.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  constructor(options: ConsentWidgetOptions = {}) {
    this.options = resolveOptions(options);
    this.consentWindow = window as WidgetWindow;
    this.dataLayer = this.consentWindow.dataLayer = this.consentWindow.dataLayer || [];

    this.installGtagFacade();
    this.setDefaultConsent();
    this.state = this.readStoredConsent();

    if (this.state) {
      this.updateGoogleConsent(this.state.choices);
    }

    this.loadGtm();
    this.bindGlobalListeners();

    if (document.body) {
      this.mount();
    } else {
      document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
    }
  }

  openSettings(): void {
    if (!this.mounted || this.destroyed) return;
    const banner = this.requireElement<HTMLElement>('[data-element="banner"]');
    const modal = this.requireElement<HTMLElement>('[data-element="modal"]');
    const dialog = this.requireElement<HTMLElement>('[data-element="dialog"]');

    this.syncInputs(this.state?.choices ?? createDeniedChoices());
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    banner.hidden = true;
    modal.hidden = false;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => dialog.focus());
  }

  getState(): ConsentEventDetail | null {
    return this.state ? this.createEventDetail(this.state) : null;
  }

  update(choices: Partial<Omit<ConsentChoices, 'necessary'>>): void {
    const current = this.state?.choices ?? createDeniedChoices();
    this.persistConsent(normalizeChoices({ ...current, ...choices }, this.options.categories), 'custom');
  }

  acceptAll(): void {
    this.persistConsent(createAcceptedChoices(this.options.categories), 'accepted_all');
  }

  rejectAll(): void {
    this.persistConsent(createDeniedChoices(), 'rejected_all');
  }

  reset(): void {
    try {
      localStorage.removeItem(this.options.storageKey);
    } catch (error) {
      this.log('Local storage could not be cleared.', error);
    }

    this.state = null;
    const denied = createDeniedChoices();
    this.pushGtag('consent', 'update', createDefaultGoogleConsent());
    this.pushDataLayerEvent('gd_consent_reset', null, denied);
    window.dispatchEvent(new CustomEvent('gd-consent:reset', {
      detail: { siteId: this.options.siteId, googleConsent: createDefaultGoogleConsent() },
    }));
    this.closeSettings(false);

    const banner = this.getElement<HTMLElement>('[data-element="banner"]');
    if (banner) banner.hidden = false;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unbindGlobalListeners();
    this.unlockPage();
    this.host?.remove();
    this.host = null;
    this.shadow = null;
    this.mounted = false;

    if (this.consentWindow.GDConsent === this) {
      delete this.consentWindow.GDConsent;
    }
  }

  private installGtagFacade(): void {
    if (typeof this.consentWindow.gtag === 'function') return;
    const layer = this.dataLayer;
    this.consentWindow.gtag = function (..._args: unknown[]) {
      layer.push(arguments);
    };
  }

  private pushGtag(...args: unknown[]): void {
    const command = (function (..._args: unknown[]) {
      return arguments;
    })(...args);
    this.dataLayer.push(command);
  }

  private setDefaultConsent(): void {
    this.pushGtag('consent', 'default', {
      ...createDefaultGoogleConsent(),
      wait_for_update: this.options.waitForUpdate,
    });
  }

  private updateGoogleConsent(choices: ConsentChoices): void {
    this.pushGtag('consent', 'update', toGoogleConsent(choices));
  }

  private loadGtm(): void {
    if (!this.options.gtmId) {
      this.log('No GTM ID supplied; consent UI runs without loading Google Tag Manager.');
      return;
    }

    if (!GTM_ID_PATTERN.test(this.options.gtmId)) {
      console.warn(`[GD Consent] Invalid GTM ID "${this.options.gtmId}". GTM was not loaded.`);
      return;
    }

    const existingGtm = document.querySelector<HTMLScriptElement>('script[src*="googletagmanager.com/gtm.js"]');
    if (existingGtm) {
      if (!existingGtm.dataset.gdConsentGtm) {
        console.warn('[GD Consent] GTM was already present. Remove the standard GTM snippet so default consent is guaranteed to run first.');
      }
      return;
    }

    this.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.dataset.gdConsentGtm = this.options.gtmId;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(this.options.gtmId)}`;
    if (this.options.nonce) script.nonce = this.options.nonce;
    document.head.appendChild(script);
  }

  private readStoredConsent(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(this.options.storageKey);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);

      if (
        !isObject(parsed)
        || parsed.schemaVersion !== 1
        || parsed.consentVersion !== this.options.consentVersion
        || !['accepted_all', 'rejected_all', 'custom'].includes(String(parsed.decision))
        || !isObject(parsed.choices)
      ) {
        localStorage.removeItem(this.options.storageKey);
        return null;
      }

      return {
        schemaVersion: 1,
        consentVersion: this.options.consentVersion,
        decision: parsed.decision as Decision,
        updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
        choices: normalizeChoices(parsed.choices as Partial<ConsentChoices>, this.options.categories),
      };
    } catch (error) {
      this.log('Stored consent could not be read.', error);
      return null;
    }
  }

  private persistConsent(choices: ConsentChoices, decision: Decision): void {
    const normalizedChoices = normalizeChoices(choices, this.options.categories);
    this.state = {
      schemaVersion: 1,
      consentVersion: this.options.consentVersion,
      decision,
      updatedAt: new Date().toISOString(),
      choices: normalizedChoices,
    };

    try {
      localStorage.setItem(this.options.storageKey, JSON.stringify(this.state));
    } catch (error) {
      this.log('Consent could not be saved to local storage.', error);
    }

    this.updateGoogleConsent(normalizedChoices);
    this.pushDataLayerEvent('gd_consent_update', this.state, normalizedChoices);
    this.syncInputs(normalizedChoices);
    this.closeSettings(false);

    const banner = this.getElement<HTMLElement>('[data-element="banner"]');
    if (banner) banner.hidden = true;

    const detail = this.createEventDetail(this.state);
    window.dispatchEvent(new CustomEvent<ConsentEventDetail>('gd-consent:changed', { detail }));
    window.dispatchEvent(new CustomEvent<ConsentEventDetail>('cookie-consent:changed', { detail }));
  }

  private createEventDetail(state: StoredConsent): ConsentEventDetail {
    return {
      ...state,
      choices: { ...state.choices },
      googleConsent: toGoogleConsent(state.choices),
      siteId: this.options.siteId,
    };
  }

  private pushDataLayerEvent(event: string, state: StoredConsent | null, choices: ConsentChoices): void {
    this.dataLayer.push({
      event,
      gd_consent: {
        siteId: this.options.siteId,
        decision: state?.decision ?? null,
        consentVersion: this.options.consentVersion,
        choices: { ...choices },
        googleConsent: state ? toGoogleConsent(choices) : createDefaultGoogleConsent(),
      },
    });
  }

  private bindGlobalListeners(): void {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('gd-consent:open-settings', this.handleOpenEvent);
    window.addEventListener('cookie-consent:open-settings', this.handleOpenEvent);
    window.addEventListener('cookieConsentOpenSettings', this.handleOpenEvent);
  }

  private unbindGlobalListeners(): void {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('gd-consent:open-settings', this.handleOpenEvent);
    window.removeEventListener('cookie-consent:open-settings', this.handleOpenEvent);
    window.removeEventListener('cookieConsentOpenSettings', this.handleOpenEvent);
  }

  private mount(): void {
    if (this.mounted || this.destroyed || !document.body) return;
    this.mounted = true;
    this.host = document.createElement('div');
    this.host.id = 'gd-consent-widget';
    this.host.lang = this.options.language;
    this.shadow = this.host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    if (this.options.nonce) style.nonce = this.options.nonce;
    style.textContent = widgetStyles;
    this.shadow.append(style);

    const container = document.createElement('div');
    container.innerHTML = this.createMarkup();
    this.shadow.append(...container.childNodes);
    this.applyContent();
    this.bindUiListeners();
    document.body.append(this.host);

    const banner = this.requireElement<HTMLElement>('[data-element="banner"]');
    banner.hidden = Boolean(this.state);
    this.syncInputs(this.state?.choices ?? createDeniedChoices());

    const initialChoices = this.state?.choices ?? createDeniedChoices();
    this.pushDataLayerEvent('gd_consent_ready', this.state, initialChoices);
    window.dispatchEvent(new CustomEvent('gd-consent:ready', {
      detail: this.state ? this.createEventDetail(this.state) : {
        siteId: this.options.siteId,
        consentVersion: this.options.consentVersion,
        choices: initialChoices,
        googleConsent: createDefaultGoogleConsent(),
        decision: null,
      },
    }));
  }

  private createMarkup(): string {
    const categoryMarkup = ['necessary', ...this.options.categories].map((category) => {
      if (category === 'necessary') {
        return `
          <div class="gd-consent__category" data-category-row="necessary">
            <span>
              <span class="gd-consent__category-name" data-text="category-necessary-name"></span>
              <span class="gd-consent__category-description" data-text="category-necessary-description"></span>
            </span>
            <span class="gd-consent__required" data-text="required"></span>
          </div>`;
      }

      return `
        <label class="gd-consent__category" data-category-row="${category}">
          <span>
            <span class="gd-consent__category-name" data-text="category-${category}-name"></span>
            <span class="gd-consent__category-description" data-text="category-${category}-description"></span>
          </span>
          <span class="gd-consent__toggle-wrap">
            <input class="gd-consent__toggle-input" type="checkbox" data-consent-input="${category}">
            <span class="gd-consent__toggle" aria-hidden="true"></span>
          </span>
        </label>`;
    }).join('');

    return `
      <section class="gd-consent__banner" data-element="banner" data-layout="${this.options.layout}" data-position="${this.options.position}" aria-labelledby="gd-consent-banner-title">
        <div class="gd-consent__banner-inner">
          <div class="gd-consent__banner-content">
            <p class="gd-consent__eyebrow" data-text="eyebrow"></p>
            <h2 class="gd-consent__title" id="gd-consent-banner-title" data-text="banner-title"></h2>
            <p class="gd-consent__copy">
              <span data-text="banner-description"></span>
              <a class="gd-consent__privacy" data-element="privacy-link" target="_self" rel="privacy-policy" data-text="privacy-link"></a>
            </p>
          </div>
          <div class="gd-consent__actions" data-label="actions">
            <button class="gd-consent__button gd-consent__button--secondary" type="button" data-action="reject-all" data-text="reject-all"></button>
            <button class="gd-consent__button gd-consent__button--primary" type="button" data-action="accept-all" data-text="accept-all"></button>
            <button class="gd-consent__button gd-consent__button--link" type="button" data-action="settings" data-text="settings"></button>
          </div>
        </div>
      </section>

      <div class="gd-consent__modal" data-element="modal" hidden>
        <button class="gd-consent__backdrop" type="button" data-action="close" tabindex="-1" aria-hidden="true"></button>
        <section class="gd-consent__dialog" data-element="dialog" role="dialog" aria-modal="true" aria-labelledby="gd-consent-settings-title" tabindex="-1">
          <button class="gd-consent__close" type="button" data-action="close" data-label="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M6.707 5.293l5.293 5.292l5.293 -5.292a1 1 0 0 1 1.414 1.414l-5.292 5.293l5.292 5.293a1 1 0 0 1 -1.414 1.414l-5.293 -5.292l-5.293 5.292a1 1 0 1 1 -1.414 -1.414l5.292 -5.293l-5.292 -5.293a1 1 0 0 1 1.414 -1.414"></path>
            </svg>
          </button>
          <h2 class="gd-consent__title" id="gd-consent-settings-title" data-text="settings-title"></h2>
          <p class="gd-consent__dialog-copy" data-text="settings-description"></p>
          <div class="gd-consent__categories">${categoryMarkup}</div>
          <div class="gd-consent__dialog-actions">
            <button class="gd-consent__button gd-consent__button--primary" type="button" data-action="save" data-text="save-selection"></button>
            <button class="gd-consent__button gd-consent__button--secondary" type="button" data-action="accept-all" data-text="accept-all"></button>
            <button class="gd-consent__button gd-consent__button--link" type="button" data-action="reject-all" data-text="reject-all"></button>
          </div>
        </section>
      </div>`;
  }

  private applyContent(): void {
    if (!this.shadow) return;
    this.host?.style.setProperty('--gd-consent-accent', this.options.accent);
    this.host?.style.setProperty('--gd-consent-accent-text', this.options.accentText);

    const text = this.options.texts;
    const values: Record<string, string> = {
      eyebrow: text.eyebrow,
      'banner-title': text.bannerTitle,
      'banner-description': `${text.bannerDescription} `,
      'privacy-link': text.privacyLink,
      'accept-all': text.acceptAll,
      'reject-all': text.rejectAll,
      settings: text.settings,
      'settings-title': text.settingsTitle,
      'settings-description': text.settingsDescription,
      'save-selection': text.saveSelection,
      required: text.required,
      'category-necessary-name': text.categories.necessary.name,
      'category-necessary-description': text.categories.necessary.description,
    };

    this.options.categories.forEach((category) => {
      values[`category-${category}-name`] = text.categories[category].name;
      values[`category-${category}-description`] = text.categories[category].description;
    });

    Object.entries(values).forEach(([key, value]) => {
      this.shadow?.querySelectorAll<HTMLElement>(`[data-text="${key}"]`).forEach((element) => {
        element.textContent = value;
      });
    });

    const privacyLink = this.requireElement<HTMLAnchorElement>('[data-element="privacy-link"]');
    if (this.options.privacyUrl) {
      privacyLink.href = this.options.privacyUrl;
    } else {
      privacyLink.hidden = true;
    }

    const close = this.requireElement<HTMLButtonElement>('[data-label="close"]');
    close.setAttribute('aria-label', text.close);
    const actions = this.requireElement<HTMLElement>('[data-label="actions"]');
    actions.setAttribute('aria-label', text.actionsLabel);
  }

  private bindUiListeners(): void {
    if (!this.shadow) return;
    this.shadow.querySelectorAll<HTMLElement>('[data-action="accept-all"]').forEach((element) => {
      element.addEventListener('click', () => this.acceptAll());
    });
    this.shadow.querySelectorAll<HTMLElement>('[data-action="reject-all"]').forEach((element) => {
      element.addEventListener('click', () => this.rejectAll());
    });
    this.requireElement<HTMLElement>('[data-action="settings"]').addEventListener('click', () => this.openSettings());
    this.requireElement<HTMLElement>('[data-action="save"]').addEventListener('click', () => this.saveSelection());
    this.shadow.querySelectorAll<HTMLElement>('[data-action="close"]').forEach((element) => {
      element.addEventListener('click', () => this.closeSettings());
    });
    this.shadow.addEventListener('keydown', this.handleKeydown);
  }

  private saveSelection(): void {
    const choices = createDeniedChoices();
    this.options.categories.forEach((category) => {
      const input = this.getElement<HTMLInputElement>(`[data-consent-input="${category}"]`);
      choices[category] = Boolean(input?.checked);
    });
    this.persistConsent(choices, 'custom');
  }

  private syncInputs(choices: ConsentChoices): void {
    this.options.categories.forEach((category: OptionalCategoryKey) => {
      const input = this.getElement<HTMLInputElement>(`[data-consent-input="${category}"]`);
      if (input) input.checked = choices[category];
    });
  }

  private closeSettings(restoreFocus = true): void {
    const modal = this.getElement<HTMLElement>('[data-element="modal"]');
    if (modal) modal.hidden = true;
    this.unlockPage();

    const banner = this.getElement<HTMLElement>('[data-element="banner"]');
    if (banner) banner.hidden = Boolean(this.state);

    if (restoreFocus) this.previouslyFocused?.focus();
    this.previouslyFocused = null;
  }

  private unlockPage(): void {
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.overflow = this.previousOverflow;
    }
  }

  private getElement<T extends Element>(selector: string): T | null {
    return this.shadow?.querySelector<T>(selector) ?? null;
  }

  private requireElement<T extends Element>(selector: string): T {
    const element = this.getElement<T>(selector);
    if (!element) throw new Error(`[GD Consent] Missing widget element: ${selector}`);
    return element;
  }

  private log(message: string, error?: unknown): void {
    if (!this.options.debug) return;
    if (error) console.warn(`[GD Consent] ${message}`, error);
    else console.info(`[GD Consent] ${message}`);
  }
}

export const initConsentWidget = (options: ConsentWidgetOptions = {}): ConsentWidgetApi => {
  const consentWindow = window as WidgetWindow;
  if (consentWindow.GDConsent) return consentWindow.GDConsent;
  const widget = new ConsentWidget(options);
  consentWindow.GDConsent = widget;
  return widget;
};
